import { Injectable } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap, withLatestFrom, filter } from 'rxjs/operators';
import { ContactsService } from '@services/contacts/contacts.service'
import { ContactsActions } from '@actions/index'
import * as fromApp from '@reducers/index'
import { LocationModel, BusinessModel, ContactModel, RequestModel, ResponseModel } from '@models/index';
import { HttpApiService } from '@services/http-api.service';
import { filterContacts } from '@utilities/function-utils';
import { RouterFacadeService } from '@services/router-facade.service';
import { MessageService } from 'primeng/api';
import Swal from 'sweetalert2';
import { saveAs } from 'file-saver';



@Injectable()
export class ContactsEffects {

  searchContactsByLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ContactsActions.searchContactsByLocation),
      map(action => action.pages),
      withLatestFrom(
        this.store.select(fromApp.getCurrentLocation),
        (pages: { page: number, size: number }, location: LocationModel) => ({
          ...pages,
          locationId: location.id
        })
      ),
      switchMap((pages) =>
        this.contactsService
          .searchContactsByLocation(pages)
          .pipe(
            map(data => ContactsActions.searchContactsSuccess({ ...data })),
            catchError(error => of(ContactsActions.contactError({ error: error.message })))
          )
      )
    )
  );

  searchContactsByQueryByLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ContactsActions.searchContactsByQueryByLocation),
      map(action => action.payload),
      withLatestFrom(
        this.store.select(fromApp.getCurrentLocation),
        (payload: { page: number, size: number, query: string }, location: LocationModel) => ({
          ...payload,
          locationId: location.id
        })
      ),
      switchMap((payload) =>
        this.contactsService
          .searchContactsByQueryByLocation(payload)
          .pipe(
            map(data => ContactsActions.searchContactsSuccess({ ...data })),
            catchError(error => of(ContactsActions.contactError({ error: error.message })))
          )
      )
    )
  );

  searchContactsByBusiness$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ContactsActions.searchContactsByBusiness),
      map(action => action.pages),
      withLatestFrom(
        this.store.select(fromApp.getSelectedBusinessProfile),
        (pages: { page: number, size: number }, business: BusinessModel) => ({
          ...pages,
          businessId: business.id
        })
      ),
      switchMap((pages) =>
        this.contactsService
          .searchContactsByBusiness(pages)
          .pipe(
            map(data => ContactsActions.searchContactsSuccess({ ...data })),
            catchError(error => of(ContactsActions.contactError({ error: error.message })))
          )
      )
    )
  );

  searchContactsByQueryByBusiness$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ContactsActions.searchContactsByQueryByBusiness),
      map(action => action.payload),
      withLatestFrom(
        this.store.select(fromApp.getSelectedBusinessProfile),
        (payload: { page: number, size: number, query: string }, business: BusinessModel) => ({
          ...payload,
          businessId: business.id
        })
      ),
      switchMap((payload) =>
        this.contactsService
          .searchContactsByQueryByBusiness(payload)
          .pipe(
            map(data => ContactsActions.searchContactsSuccess({ ...data })),
            catchError(error => of(ContactsActions.contactError({ error: error.message })))
          )
      )
    )
  );

  createContact$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ContactsActions.createContacts),
      withLatestFrom(
        this.store.select(fromApp.getCurrentLocation),
        this.store.select(fromApp.getSelectedBusinessProfile),
        (contact: { contact: ContactModel, campaignId: string }, location: LocationModel, business: BusinessModel) =>
        ({
          contact: {
            ...contact.contact,
            locationId: location.id,
            businessId: business.id
          },
          campaignId: contact.campaignId
        })
      ),
      switchMap(contact =>
        this.contactsService
          .createContact(contact)
          .pipe(
            tap(data =>
              this.launchAlertMssg(`${data.status}`, `${data.message}`, data.status === 'Sent' ? 'success' : data.status === 'In queue' ? 'warning' : 'error')
            ),
            filter(data => data.status !== 'Duplicate'),
            map(data => data.data),
            map(contact => ContactsActions.createContactsSuccess({ contact })),
            catchError(error => of(ContactsActions.contactError({ error: error.message })))
          )
      )
    )
  );

  bulkAddContacts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ContactsActions.bulkAddContacts),
      map(action => action.payload),
      map(payload => {
        const contactsFilter = filterContacts(payload.map(item => item.contact))
        return contactsFilter.map(item => ({
          contact: item,
          campaignId: payload[0].campaignId
        }))
      }),
      withLatestFrom(
        this.store.select(fromApp.getCurrentLocation),
        this.store.select(fromApp.getSelectedBusinessProfile),
        (payload: { contact: ContactModel, campaignId: string }[], location: LocationModel, business: BusinessModel) => payload.map(data => {
          return ({
            contact: {
              ...data.contact,
              locationId: location.id,
              businessId: business.id
            },
            campaignId: data.campaignId
          })
        })
      ),
      switchMap(contacts =>
        this.contactsService
          .bulkAddContacts(contacts)
          .pipe(
            map(result => ContactsActions.bulkAddContactsSuccess({ result })),
            catchError(error => of(ContactsActions.contactError({ error: error.message })))
          )
      )
    )
  );

  editContact$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ContactsActions.editContact),
      switchMap(contactModel =>
        this.contactsService
          .editContact(contactModel)
          .pipe(
            tap(contact => this.launchToastMssg('success', 'Edit Contact', `Contact ${contact.name} ${contact.lastName} has been edited`)),
            map(contact => ContactsActions.editContactSuccess({ contact })),
            catchError(error => of(ContactsActions.contactError({ error: error.message })))
          )
      )
    ));

  deleteContact$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ContactsActions.deleteContact),
      map(action => action.contactId),
      withLatestFrom(
        this.store.select(fromApp.getCurrentLocation),
        (contactId: string, location: LocationModel) => ({
          ...{},
          contactId: contactId,
          locationId: location.id
        })
      ),
      switchMap(payload =>
        this.contactsService
          .deleteContact(payload)
          .pipe(
            tap(contact => this.launchToastMssg('success', 'Delete Contact', 'The contact has been deleted')),
            map(contactId => ContactsActions.deleteContactSuccess({ contactId })),
            catchError(error => of(ContactsActions.contactError({ error: error.message })))
          )
      )
    )
  );

  filterContactsByStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ContactsActions.filterContactsByStatus),
      map(action => action.request),
      withLatestFrom(
        this.store.select(fromApp.getCurrentLocation),
        (request: RequestModel, location: LocationModel) => ({
          ...request,
          params: request.params + `&locationId=${location.id}`
        })
      ),
      switchMap((request) =>
        this.httpApiService.postRequest(request)
          .pipe(
            map((data) => data as { data: ContactModel[], total: number }),
            map(data => ContactsActions.searchContactsSuccess({ ...data })),
            catchError(error => of(ContactsActions.contactError({ error: error.message })))
          )
      )
    )
  );

  filterContactsByStatusBusiness$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ContactsActions.filterContactsByStatusBusiness),
      map(action => action.request),
      withLatestFrom(
        this.store.select(fromApp.getSelectedBusinessProfile),
        (request: RequestModel, business: BusinessModel) => ({
          ...request,
          params: request.params + `&id=${business.id}`
        })
      ),
      switchMap((request) =>
        this.httpApiService.postRequest(request)
          .pipe(
            map((data) => data as { data: ContactModel[], total: number }),
            map(data => ContactsActions.searchContactsSuccess({ ...data })),
            catchError(error => of(ContactsActions.contactError({ error: error.message })))
          )
      )
    )
  );

  exportDataContact$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ContactsActions.exportDataContact),
      map(action => action.request),
      switchMap((request) =>
        this.httpApiService.getRequestOctetStream(request)
          .pipe(
            tap(resp => {
              saveAs(resp, `contact.csv`)
            }),
            map(() => ContactsActions.exportDataContactSuccess({ response: true })),
            catchError(error => {
              this.launchToastMssg('error', 'Export Contacts', `${error.error.message}`)
              return of(ContactsActions.contactError({ error: error.message }))
            })
          )
      )
    )
  );

  exportDataContactCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ContactsActions.exportDataContactCount),
      map(action => action.request),
      switchMap((request) =>
        this.httpApiService.getRequest(request)
          .pipe(
            map(data => data as { count: number }),
            map(data => ContactsActions.exportDataContactCountSuccess({ data })),
            catchError(error => of(ContactsActions.contactError({ error: error.message })))
          )
      )
    )
  );
  constructor(
    private actions$: Actions,
    private contactsService: ContactsService,
    private messageToast: MessageService,
    private httpApiService: HttpApiService,
    private routerFacadeService: RouterFacadeService,
    private store: Store<any>
  ) { }

  launchToastMssg(severity: string, summary: string, detail: string) {
    this.messageToast.add({ severity: severity, summary: summary, detail: detail, life: 5000 });
  }

  launchAlertMssg(title: string, message: string, type: any) {
    Swal.fire({
      title: title,
      html: message,
      icon: type,
      showCancelButton: true,
      confirmButtonText: 'View Contacts',
      cancelButtonText: 'Close',
      footer: '<a href="admin-location/campaigns"><b><u>Edit Sending Settings</u></b></a>'
    }).then(result => {
      if (result.value) this.routerFacadeService.navigateTo$('admin-location/contacts')
    })
  }
}
